import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, Fab, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FixedAssetList from "./FixedAssetList";
import FixedAssetDetail from "./FixedAssetDetail";
import AddFixedAssetModal from "./AddFixedAssetModal";
import TransferAssetModal from "./TransferAssetModal";
import AssetFilterModal from "./components/AssetFilterModal";

const INITIAL_FILTERS = { location: "", job: "", employee: "", jobId: "", employeeId: "" };

const FixedAssets = () => {
  const { selectedClientData, selectedAppPermissions } = useSelector((state) => state?.main);
  const isAdmin = selectedClientData?.IsAdmin === 1;
  const isEmployee = Boolean(selectedClientData?.ClientEmployeeId);
  const employeePermissions = selectedAppPermissions || [];
  
  const hasElevatedPermission = employeePermissions.some((perm) => {
    if (Number(perm?.AppId) !== 21) return false;
    const normalized = (perm?.PermissionName || "").toLowerCase().replace(/\s+/g, "");
    return ["add", "add_edit", "addedit", "all", "fullcontrol"].includes(normalized);
  });
  const canViewAllAssets = isAdmin || (isEmployee && hasElevatedPermission);

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [fabMenuAnchorEl, setFabMenuAnchorEl] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...INITIAL_FILTERS }));
  const [draftFilters, setDraftFilters] = useState(() => ({ ...INITIAL_FILTERS }));
  const [selectedFilterOption, setSelectedFilterOption] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  const handleRefreshList = () => setRefreshKey(prev => prev + 1);

  const handleClearFilter = (key) => {
    const reset = { ...appliedFilters, [key]: "" };
    if (key === "job") reset.jobId = "";
    if (key === "employee") reset.employeeId = "";
    setAppliedFilters(reset);
    setDraftFilters(reset);
  };

  const handleClearAllFilters = () => {
    setAppliedFilters(INITIAL_FILTERS);
    setDraftFilters(INITIAL_FILTERS);
  };

  const determineSelectedOption = (filters) => {
    if (filters?.job || filters?.jobId) return "job";
    if (filters?.employee || filters?.employeeId) return "employee";
    if (filters?.location) return "location";
    return null;
  };

  const openFilterModal = () => {
    setDraftFilters({ ...appliedFilters });
    setSelectedFilterOption(determineSelectedOption(appliedFilters));
    setFilterModalOpen(true);
  };

  const handleFilterApply = () => {
    setAppliedFilters({ ...draftFilters });
    setFilterModalOpen(false);
  };

  const handleFabOpen = (event) => {
    setFabMenuAnchorEl(event.currentTarget);
  };

  const handleFabClose = () => {
    setFabMenuAnchorEl(null);
  };

  const handleAddNewAsset = () => {
    handleFabClose();
    setAddModalOpen(true);
  };

  const handleTransferAsset = () => {
    handleFabClose();
    setTransferModalOpen(true);
  };

  const handleTransferClose = (shouldRefresh) => {
    setTransferModalOpen(false);
    if (shouldRefresh) {
      handleRefreshList(); // Or refresh detail if needed
      setDetailRefreshKey(prev => prev + 1);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f5f7fb", minHeight: "calc(100vh - 110px)", pb: 4, overflow: "scroll", scrollbarWidth: "none" }}>
      <Box sx={{ px: 2, pt: 2 }}>
        {selectedAsset ? (
          <FixedAssetDetail
            key={selectedAsset?.AssetId}
            selectedAsset={selectedAsset}
            onBack={() => setSelectedAsset(null)}
            onTransferClick={() => setTransferModalOpen(true)}
            refreshToken={detailRefreshKey}
          />
        ) : (
          <FixedAssetList
            key={refreshKey}
            onSelectAsset={(asset) => setSelectedAsset(asset)}
            appliedFilters={appliedFilters}
            onOpenFilter={openFilterModal}
            onClearFilter={handleClearFilter}
            onClearAllFilters={handleClearAllFilters}
            restrictToOwnAssets={!canViewAllAssets}
          />
        )}
      </Box>

      {canViewAllAssets && !selectedAsset && (
        <>
          <Fab
            color="primary"
            onClick={handleFabOpen}
            aria-label="asset actions"
            aria-controls={fabMenuAnchorEl ? "fixed-assets-fab-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={fabMenuAnchorEl ? "true" : undefined}
            sx={{ position: "fixed", bottom: 24, right: 24, color: 'white', background: selectedClientData?.AccentColor, "&:hover": { bgcolor: selectedClientData?.AccentColor } }}
          >
            <AddIcon />
          </Fab>

          <Menu
            id="fixed-assets-fab-menu"
            anchorEl={fabMenuAnchorEl}
            open={Boolean(fabMenuAnchorEl)}
            onClose={handleFabClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1.25,
                minWidth: 220,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(148, 163, 184, 0.18)",
                boxShadow: "0px 14px 34px rgba(15, 23, 42, 0.16)",
              },
            }}
            MenuListProps={{
              dense: true,
              sx: { py: 0.75 },
            }}
          >
            <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, letterSpacing: 0.6 }}>
                Quick Actions
              </Typography>
            </Box>
            {
              hasElevatedPermission && (
                <MenuItem
                  onClick={handleAddNewAsset}
                  sx={{ py: 1.1, px: 2, gap: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: selectedClientData?.AccentColor }}>
                    <AddCircleOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Add New Asset"
                    primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }}
                  />
                </MenuItem>
              )
            }

            {isAdmin && (
              <MenuItem
                onClick={handleTransferAsset}
                sx={{ py: 1.1, px: 2, gap: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: selectedClientData?.AccentColor }}>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Transfer Asset"
                  primaryTypographyProps={{ fontSize: "0.95rem", fontWeight: 600 }}
                />
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      {addModalOpen && (
        <AddFixedAssetModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onRefresh={handleRefreshList}
        />
      )}

      {filterModalOpen && (
        <AssetFilterModal
          filterModalOpen={filterModalOpen}
          setFilterModalOpen={setFilterModalOpen}
          draftFilters={draftFilters}
          setDraftFilters={setDraftFilters}
          handleClearAllFilters={handleClearAllFilters}
          handleFilterApply={handleFilterApply}
          selectedFilterOption={selectedFilterOption}
          setSelectedFilterOption={setSelectedFilterOption}
          accentColor={selectedClientData?.AccentColor}
          canViewAllAssets={canViewAllAssets}
          empID={selectedClientData?.ClientEmployeeId}
        />
      )}

      {transferModalOpen && (
        <TransferAssetModal
          open={transferModalOpen}
          onClose={handleTransferClose}
          selectedAsset={selectedAsset}
        />
      )}
    </Box>
  );
};

export default FixedAssets;
